/**
 * StaffTaskNowPanel — 원장·총무가 '지금 무엇을 해야 하고, 무엇을 놓쳤는지' 한눈에 보는 칸 (2026-10-01 대표 지시)
 *
 * 종전 대시보드는 하루 업무 130여 개를 시간순으로 늘어놓은 목록이라, 지금 할 일·놓친 일이 묻혔다.
 * 이 칸은 진행률 한 줄 + 숫자 세 개(지금 할 일 / 놓친 일 / 순찰)만 보여주고, 숫자를 누르면 그 목록 하나만 펼친다.
 * (2026-10-02 대표 "더 깔끔하게, 심플하게" — 색 상자·구역 칩·세부 질문을 걷어냄)
 *
 * 순찰 칸(총무 업무에서 '🚶 QR 순찰'로 표시한 칸)은 같은 시간대를 한 회차로 묶어 보여주고,
 * '구역 1/2' 처럼 찍은 구역 수만 붙는다. 모든 구역이 찍히면 서버가 자동으로 체크한다.
 * 데이터는 총무 업무 하나(/staff-tasks/today) — 순찰 일정을 따로 두지 않는다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../../../config/api';
import { authedFetch } from '../../../../infrastructure/api/authedFetch';

type TaskStatus = 'done' | 'now' | 'overdue' | 'upcoming';
type View = 'now' | 'missed' | 'patrol';

interface PatrolZone { name: string; status: string | null; scanned_at: string | null; staff_name: string | null }
interface PatrolInfo { zones_total: number; zones_done: number; zones: PatrolZone[] }

interface Task {
  id: string;
  start_time: string;
  end_time: string | null;
  place: string;
  content: string;
  status: TaskStatus;
  checked_at: string | null;
  checked_by_name: string;
  note: string;
  is_patrol?: boolean;
  patrol?: PatrolInfo | null;
}

interface Today {
  date: string;
  now: string;
  is_today: boolean;
  total: number;
  done: number;
  overdue: number;
  items: Task[];
}

/** 화면에서 한 줄 = 일반 업무 1개, 또는 같은 시간대 순찰 칸 묶음(한 회차) */
interface Row {
  key: string;
  patrol: boolean;
  start: string;
  end: string | null;
  title: string;
  tasks: Task[];
  status: TaskStatus;
  info?: PatrolInfo | null;
}

function toRows(items: Task[]): Row[] {
  const rows: Row[] = [];
  const byKey: Record<string, Row> = {};
  for (const t of items) {
    if (t.is_patrol) {
      const key = `p:${t.start_time}-${t.end_time || ''}`;
      if (!byKey[key]) {
        byKey[key] = { key, patrol: true, start: t.start_time, end: t.end_time, title: t.place || '순찰', tasks: [], status: t.status, info: t.patrol };
        rows.push(byKey[key]);
      }
      byKey[key].tasks.push(t);
    } else {
      rows.push({ key: t.id, patrol: false, start: t.start_time, end: t.end_time, title: t.content, tasks: [t], status: t.status });
    }
  }
  // 묶음 상태: 하나라도 안 끝났으면 그 칸의 상태(지금/지남/대기), 다 끝났으면 완료
  for (const r of rows) {
    const open = r.tasks.filter((t) => t.status !== 'done');
    r.status = open.length === 0 ? 'done' : open[0].status;
  }
  return rows;
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column' as const, gap: 12 },
  bar: { height: 8, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' },
  chips: { display: 'flex', gap: 6, flexWrap: 'wrap' as const, alignItems: 'center' },
  sec: { borderRadius: 10, padding: '10px 12px' },
  secHead: { fontWeight: 700, fontSize: 14, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 },
  row: { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '7px 0', borderTop: '1px solid rgba(0,0,0,0.06)' },
  time: { minWidth: 92, fontSize: 13, fontWeight: 700, color: '#374151' },
  body: { flex: 1, minWidth: 0, fontSize: 14, color: '#111827' },
  sub: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  btn: { border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, padding: '5px 10px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' as const },
};

const StaffTaskNowPanel: React.FC<{ compact?: boolean; onChanged?: () => void }> = ({ compact, onChanged }) => {
  const [data, setData] = useState<Today | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [view, setView] = useState<View>('now');
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await authedFetch(`${API_BASE_URL}/api/v1/owner/staff-tasks/today`);
      if (res.ok) setData((await res.json()).data || null);
    } catch {
      /* 대시보드 나머지는 계속 떠야 한다 */
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);   // 시간이 흐르면 '지금'·'놓친 일'이 따라 움직인다
    return () => clearInterval(t);
  }, [load]);

  const rows = useMemo(() => toRows(data?.items || []), [data]);

  const check = async (row: Row, undo: boolean) => {
    if (busy) return;
    setBusy(row.key);
    try {
      const targets = undo ? row.tasks.filter((t) => t.status === 'done') : row.tasks.filter((t) => t.status !== 'done');
      for (const t of targets) {
        await authedFetch(`${API_BASE_URL}/api/v1/owner/staff-tasks/${t.id}/check`, undo
          ? { method: 'DELETE' }
          : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: '' }) });
      }
      await load();
      onChanged?.();
    } finally {
      setBusy(null);
    }
  };

  if (!loaded) return null;
  if (!data || data.total === 0) {
    return <div style={{ fontSize: 14, color: '#6b7280' }}>오늘 할 총무 업무가 없습니다. 항목은 [총무 업무 편집]에서 추가합니다.</div>;
  }

  const nowRows = rows.filter((r) => r.status === 'now');
  const missedRows = rows.filter((r) => r.status === 'overdue');
  const nextStart = rows.find((r) => r.status === 'upcoming')?.start;
  const nextRows = nextStart ? rows.filter((r) => r.status === 'upcoming' && r.start === nextStart) : [];
  const patrolRows = rows.filter((r) => r.patrol);
  const patrolDone = patrolRows.filter((r) => r.status === 'done').length;
  const pct = Math.round((data.done / data.total) * 100);

  // 한 번에 목록 하나만 — 위 숫자를 누르면 바뀐다 (2026-10-02 대표 "더 심플하게")
  const lists: Record<View, Row[]> = { now: nowRows.length ? nowRows : nextRows, missed: missedRows, patrol: patrolRows };
  const list = lists[view];
  const limit = expanded ? list.length : compact ? 5 : 10;

  const stat = (key: View, label: string, value: string, warn?: boolean) => {
    const on = view === key;
    return (
      <button key={key} onClick={() => { setView(key); setExpanded(false); }} style={{
        flex: 1, textAlign: 'left', cursor: 'pointer', borderRadius: 10, padding: '8px 12px',
        border: on ? '2px solid #111827' : '1px solid #e5e7eb', background: '#fff',
      }}>
        <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: warn ? '#dc2626' : '#111827' }}>{value}</div>
      </button>
    );
  };

  const statusMark = (r: Row) => {
    if (r.status === 'done') return <span style={{ color: '#16a34a', fontWeight: 700 }}>완료</span>;
    if (r.status === 'overdue') return <span style={{ color: '#dc2626', fontWeight: 700 }}>놓침</span>;
    if (r.status === 'upcoming') return <span style={{ color: '#9ca3af' }}>예정</span>;
    return null;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', marginBottom: 6 }}>
          <span><b style={{ color: '#111827' }}>{data.done}/{data.total}</b> 완료</span>
          <span>{data.now}</span>
        </div>
        <div style={S.bar}><div style={{ width: `${pct}%`, height: '100%', background: '#111827' }} /></div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        {stat('now', nowRows.length ? '지금 할 일' : `다음 ${nextStart || ''}`.trim(), String(nowRows.length ? nowRows.length : nextRows.length))}
        {stat('missed', '놓친 일', String(missedRows.length), missedRows.length > 0)}
        {patrolRows.length > 0 && stat('patrol', '순찰', `${patrolDone}/${patrolRows.length}`)}
      </div>

      <div>
        {list.length === 0 && (
          <div style={{ fontSize: 13, color: '#9ca3af', padding: '8px 0' }}>
            {view === 'missed' ? '놓친 일이 없습니다.' : '남은 업무가 없습니다.'}
          </div>
        )}
        {list.slice(0, limit).map((r) => {
          const canCheck = r.status === 'now' || r.status === 'overdue';
          return (
            <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderTop: '1px solid #f3f4f6' }}>
              <span style={{ minWidth: 44, fontSize: 13, fontWeight: 700, color: '#374151' }}>{r.start}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 14, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.patrol ? `🚶 ${r.title}` : r.title}
              </span>
              {r.patrol && r.info && r.info.zones_total > 0 && (
                <span style={{ fontSize: 12, color: r.info.zones_done === r.info.zones_total ? '#16a34a' : '#6b7280' }}>
                  구역 {r.info.zones_done}/{r.info.zones_total}
                </span>
              )}
              {view === 'patrol' && statusMark(r)}
              {canCheck && (
                <button style={S.btn} disabled={busy === r.key} onClick={() => check(r, false)}>체크</button>
              )}
            </div>
          );
        })}
        {list.length > limit && (
          <button style={{ ...S.btn, border: 'none', color: '#2563eb', padding: '6px 0' }} onClick={() => setExpanded(true)}>
            {list.length - limit}개 더 보기
          </button>
        )}
      </div>
    </div>
  );
};

export default StaffTaskNowPanel;
